import type {
  DeliveryPlanStatus,
  PlannerEligibleOrderLineDto,
} from "../services/deliveryPlansApi";

export function getPlanStructuralEditRestriction(status?: DeliveryPlanStatus): string | undefined {
  if (!status || status === "Draft" || status === "Scheduled") return undefined;
  if (status === "InProgress") return "This delivery plan has already started.";
  if (status === "Completed" || status === "PartiallyCompleted") return "This delivery plan has already been completed.";
  if (status === "Cancelled") return "This delivery plan has been cancelled.";
  if (status === "ReconciliationRequired") return "This delivery plan is awaiting reconciliation.";
  return "This delivery plan has already been published.";
}

export function getOrderAddRestriction({
  selected,
  availableToPlan,
  requiresRouteStop,
  selectedRouteId,
  hasPlanningStop,
  planStatus,
}: {
  selected: boolean;
  availableToPlan: number;
  requiresRouteStop?: boolean;
  selectedRouteId?: string;
  hasPlanningStop: boolean;
  planStatus?: DeliveryPlanStatus;
}): string | undefined {
  if (selected) return undefined;

  const planRestriction = getPlanStructuralEditRestriction(planStatus);
  if (planRestriction) return planRestriction;
  if (availableToPlan <= 0) return "No remaining quantity is available to plan.";
  if (selectedRouteId && requiresRouteStop && !hasPlanningStop)
    return "This order is not configured for a stop on the selected route.";

  return undefined;
}

export function getStopEtaRestriction({
  date,
  time,
  plannedDeparture,
  orderExpectedDeliveries,
}: {
  date: string;
  time: string;
  plannedDeparture?: string;
  orderExpectedDeliveries: Array<string | undefined>;
}): string | undefined {
  if (!date && !time) return undefined;
  if (!date || !time) return "Choose both an ETA date and time, or leave both blank.";

  const eta = new Date(`${date}T${time}`);
  if (Number.isNaN(eta.getTime())) return "Choose a valid stop ETA.";

  const departure = plannedDeparture ? new Date(plannedDeparture) : undefined;
  if (departure && !Number.isNaN(departure.getTime())) {
    if (eta <= departure)
      return "Stop ETA must be after the planned departure.";
  }

  const earliestOrderExpectation = orderExpectedDeliveries
    .filter((value): value is string => !!value)
    .map((value) => new Date(value))
    .filter((value) => !Number.isNaN(value.getTime()))
    .sort((left, right) => left.getTime() - right.getTime())[0];

  const expectationStillReachable = earliestOrderExpectation
    && (!departure || Number.isNaN(departure.getTime()) || earliestOrderExpectation > departure);
  if (expectationStillReachable && eta > earliestOrderExpectation)
    return "Stop ETA must not be later than the order's expected delivery time.";

  return undefined;
}

const normalizeVehicleRegistration = (registration?: string) =>
  registration?.trim().toUpperCase().replace(/[^A-Z0-9]/g, "") ?? "";

export function findAssignedVehicleId(
  defaultVehicleReg: string | undefined,
  vehicles: Array<{ id: string; registrationNumber: string; status?: string }>,
): string | undefined {
  const normalizedDefault = normalizeVehicleRegistration(defaultVehicleReg);
  if (!normalizedDefault) return undefined;

  return vehicles.find(
    (vehicle) => normalizeVehicleRegistration(vehicle.registrationNumber) === normalizedDefault
      && isVehicleAvailableForPlanning(vehicle.status),
  )?.id;
}

export function isVehicleAvailableForPlanning(status?: string): boolean {
  return !["unavailable", "maintenance", "breakdown"].includes(status?.trim().toLowerCase() ?? "");
}

const formatQuantity = (amount: number) =>
  amount.toLocaleString("en-KE", { maximumFractionDigits: 2 });

export function formatOrderLinePackaging(
  line: Pick<PlannerEligibleOrderLineDto, "trayType" | "unitName">,
): string {
  if (line.trayType) {
    const trayTypeLabels: Record<string, string> = {
      TwoTenHole: "210-Hole Tray",
      FourFortyTwoHole: "442-Hole Tray",
      "210": "210-Hole Tray",
      "442": "442-Hole Tray",
    };

    return trayTypeLabels[line.trayType] ?? line.trayType;
  }

  const unitName = line.unitName.trim();
  if (unitName.toLowerCase().includes("piece")) return "Pieces";
  if (unitName.toLowerCase().includes("tray")) return "Trays";
  return unitName || "Units";
}

export function formatQuantityBreakdown<T extends Pick<PlannerEligibleOrderLineDto, "unitName">>(
  lines: T[],
  getQuantity: (line: T) => number,
): string {
  let trays = 0;
  let pieces = 0;
  const otherUnits = new Map<string, number>();

  lines.forEach((line) => {
    const quantity = getQuantity(line);
    const normalizedUnit = line.unitName.trim().toLowerCase();
    if (normalizedUnit.includes("tray")) trays += quantity;
    else if (normalizedUnit.includes("piece")) pieces += quantity;
    else otherUnits.set(line.unitName || "units", (otherUnits.get(line.unitName || "units") ?? 0) + quantity);
  });

  const parts: string[] = [];
  if (trays > 0) parts.push(`${formatQuantity(trays)} ${trays === 1 ? "tray" : "trays"}`);
  if (pieces > 0) parts.push(`${formatQuantity(pieces)} ${pieces === 1 ? "piece" : "pieces"}`);
  otherUnits.forEach((quantity, unit) => parts.push(`${formatQuantity(quantity)} ${unit}`));
  return parts.join(" · ") || "0 units";
}

export function clampPlanningQuantity(rawValue: string, availableToPlan: number): number {
  const parsed = Number(rawValue);
  const nonNegativeQuantity = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  return Math.min(nonNegativeQuantity, availableToPlan);
}

export function groupOrdersByStop<
  T extends { orderId: string; customerId: string; deliveryLocation: string; routeStopName?: string },
>(
  orders: T[],
  resolveRouteStop: (order: T) => { id: string; stopName: string } | undefined,
): Array<{ key: string; name: string; orders: T[] }> {
  const groups = new Map<string, { key: string; name: string; orders: T[] }>();

  orders.forEach((order) => {
    const routeStop = resolveRouteStop(order);
    const key = routeStop?.id ?? (order.deliveryLocation.trim().toLowerCase() || order.orderId);
    const current = groups.get(key) ?? {
      key,
      name: routeStop?.stopName ?? order.routeStopName ?? order.deliveryLocation ?? "Unmapped destination",
      orders: [],
    };
    current.orders.push(order);
    groups.set(key, current);
  });

  return [...groups.values()];
}
